import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';

import { useToastr } from '../../toastr/toastr-context';
import { getCroppedBlob } from './get-cropped-blob';

export interface ImageCropDialogProps {
  open: boolean;
  imageSrc: string;
  aspect: number;
  cropShape?: 'rect' | 'round';
  mimeType?: string;
  onCancel: () => void;
  onCropped: (blob: Blob) => void;
}

export function ImageCropDialog({
  open,
  imageSrc,
  aspect,
  cropShape = 'rect',
  mimeType = undefined,
  onCancel,
  onCropped,
}: ImageCropDialogProps) {
  const { push } = useToastr();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_a: Area, areaPx: Area) => {
    setAreaPixels(areaPx);
  }, []);

  const handleSave = useCallback(async () => {
    if (!areaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels, mimeType);
      onCropped(blob);
    } catch (err) {
      push({
        title: 'Could not crop image',
        body: err instanceof Error ? err.message : 'An unknown error occurred.',
        bg: 'danger',
      });
    } finally {
      setSaving(false);
    }
  }, [areaPixels, imageSrc, mimeType, onCropped, push]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Position &amp; crop image</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 320,
            bgcolor: 'grey.900',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={cropShape === 'rect'}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Drag to reposition · use the slider to zoom
          </Typography>
          <Slider
            aria-label="Zoom"
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={(_e, v) => setZoom(v as number)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleSave();
          }}
          disabled={saving || !areaPixels}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
